import React, { useState, useEffect } from 'react';
import {
    Box, Paper, Switch, FormControlLabel, Button, Stack, Divider,
    Typography, Popover, ToggleButtonGroup, ToggleButton, Menu, MenuItem, ListItemIcon, ListItemText, LinearProgress, Collapse
} from '@mui/material';
import { DateObject, Calendar } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";

// آیکون‌ها
import EventNoteIcon from '@mui/icons-material/EventNote';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import AutorenewIcon from '@mui/icons-material/Autorenew';

const FilterBar = ({ onFilterChange, initialFilters }) => {
    // State های موقت برای داخل پنل
    const [tempDates, setTempDates] = useState(initialFilters.dates);
    const [tempCompare, setTempCompare] = useState(initialFilters.compare);
    const [activeFilter, setActiveFilter] = useState('month');

    // State جدید برای تاریخ بخش دانلود
    const [reportDates, setReportDates] = useState(initialFilters.dates);

    const [autoRefresh, setAutoRefresh] = useState({ enabled: false, interval: 30000 });

    const [anchorEl, setAnchorEl] = useState(null);
    const [popoverType, setPopoverType] = useState('');
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);

    const handleOpenPopover = (event, type) => {
        setPopoverType(type);
        setAnchorEl(event.currentTarget);
    };

    const handleClosePopover = () => {
        setAnchorEl(null);
    };

    const handleApplyFilters = () => {
        onFilterChange({ dates: tempDates, compare: tempCompare });
        handleClosePopover();
    };

    const handleDateFilterClick = (event, newFilter) => {
        if (newFilter !== null) {
            setActiveFilter(newFilter);
            const today = new DateObject({ calendar: persian });
            let fromDate, toDate = new DateObject({ date: today });
            if (newFilter === 'today') fromDate = new DateObject({ date: today });
            else if (newFilter === 'week') fromDate = new DateObject({ date: today }).subtract(today.weekDay, "days");
            else if (newFilter === 'month') fromDate = new DateObject({ date: today }).set('day', 1);
            setTempDates([fromDate, toDate]);
        }
    };

    const handleDownload = (format) => {
        if (!reportDates || reportDates.length !== 2) {
            alert("لطفا بازه زمانی گزارش را برای دانلود مشخص کنید.");
            return;
        }
        handleClosePopover();
        setIsDownloading(true);
        setDownloadProgress(0);

        const progressInterval = setInterval(() => {
            setDownloadProgress(prev => Math.min(prev + 10, 99));
        }, 100);

        const [after, before] = reportDates;
        const gregorian_after = after.convert('gregorian');
        const gregorian_before = before.convert('gregorian');
        const afterStr = `${gregorian_after.year}-${String(gregorian_after.month.number).padStart(2, '0')}-${String(gregorian_after.day).padStart(2, '0')}`;
        const beforeStr = `${gregorian_before.year}-${String(gregorian_before.month.number).padStart(2, '0')}-${String(gregorian_before.day).padStart(2, '0')}`;

        const url = `/db/download_report.php?format=${format}&after=${afterStr}&before=${beforeStr}`;

        fetch(url)
            .then(res => {
                if (!res.ok) throw new Error('Network response was not ok.');
                return res.blob();
            })
            .then(blob => {
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = blobUrl;
                a.download = `report_${afterStr}_to_${beforeStr}.${format === 'excel' ? 'csv' : 'pdf'}`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(blobUrl);
                a.remove();

                clearInterval(progressInterval);
                setDownloadProgress(100);
                setTimeout(() => setIsDownloading(false), 1500);
            })
            .catch(err => {
                console.error("Download Error:", err);
                clearInterval(progressInterval);
                setIsDownloading(false);
                alert("خطا در دانلود گزارش.");
            });
    };

    useEffect(() => {
        let intervalId = null;
        if (autoRefresh.enabled && autoRefresh.interval) {
            intervalId = setInterval(() => {
                onFilterChange({ dates: tempDates, compare: tempCompare });
            }, autoRefresh.interval);
        }
        return () => clearInterval(intervalId);
    }, [autoRefresh, onFilterChange, tempDates, tempCompare]);

    // !! اصلاح کلیدی: جلوگیری از خطای format در هنگام انتخاب بازه !!
    const getSafeDateRangeLabel = () => {
        if (tempDates && tempDates.length === 2 && tempDates[0] && tempDates[1]) {
            return `${tempDates[0].format("D MMMM")} - ${tempDates[1].format("D MMMM YYYY")}`;
        }
        return "در حال انتخاب...";
    };

    return (
        <>
            <style>{`
                .bg-dark { background: transparent !important; box-shadow: none !important; }
                .bg-dark .rmdp-header, .bg-dark .rmdp-week-day, .bg-dark .rmdp-day.rmdp-disabled, .bg-dark .rmdp-arrow { color: rgba(255,255,255,0.5) !important; }
                .bg-dark .rmdp-day:not(.rmdp-disabled):not(.rmdp-selected) span:hover { background-color: rgba(255,255,255,0.2) !important; }
                .bg-dark .rmdp-arrow-container:hover { background-color: rgba(255,255,255,0.1) !important; }
                .bg-dark .rmdp-day.rmdp-today span { background-color: rgba(97, 218, 251, 0.3); }
            `}</style>

            <Paper
                sx={{
                    p: 1, mb: 4, background: 'rgba(12, 19, 29, 0.7)',
                    backdropFilter: 'blur(16px)', borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden'
                }}
            >
                <Stack direction="row" divider={<Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: 1 }} />} sx={{ width: '100%' }}>
                    <Button onClick={(e) => handleOpenPopover(e, 'date')} fullWidth sx={{ color: '#fff', p: 1.5, display: 'flex', justifyContent: 'flex-start', gap: 1.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }} >
                        <EventNoteIcon />
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>بازه زمانی</Typography>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{getSafeDateRangeLabel()}</Typography>
                        </Box>
                    </Button>
                    <Button onClick={(e) => handleOpenPopover(e, 'compare')} fullWidth sx={{ color: '#fff', p: 1.5, display: 'flex', justifyContent: 'flex-start', gap: 1.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }} >
                        <CompareArrowsIcon />
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>مقایسه</Typography>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{tempCompare ? 'فعال' : 'غیرفعال'}</Typography>
                        </Box>
                    </Button>
                    <Button onClick={(e) => handleOpenPopover(e, 'refresh')} fullWidth sx={{ color: '#fff', p: 1.5, display: 'flex', justifyContent: 'flex-start', gap: 1.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }} >
                        <AutorenewIcon />
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>بروزرسانی خودکار</Typography>
                    </Button>
                    <Button onClick={(e) => handleOpenPopover(e, 'download')} fullWidth sx={{ color: '#fff', p: 1.5, display: 'flex', justifyContent: 'flex-start', gap: 1.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }} >
                        <DownloadForOfflineOutlinedIcon />
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>دانلود گزارش</Typography>
                    </Button>
                </Stack>
                {isDownloading && <LinearProgress variant="determinate" value={downloadProgress} sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px' }} />}
            </Paper>

            <Popover
                open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleClosePopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{ sx: { p: 3, width: 'auto', bgcolor: 'rgba(30, 43, 58, 0.9)', color: '#fff', boxShadow: 24, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)', backdropFilter: 'blur(16px)', mt: 1.5 } }}
            >
                {popoverType === 'date' && (
                    <Stack spacing={2}>
                        <Typography variant="h6">انتخاب بازه زمانی</Typography>
                        <ToggleButtonGroup
                            value={activeFilter}
                            exclusive
                            onChange={handleDateFilterClick}
                            fullWidth
                            color="primary"
                            sx={{
                                gap: '10px',
                                '& .MuiToggleButton-root': {
                                    borderRadius: '10px !important',
                                    border: '1px solid rgba(255,255,255,0.23) !important'
                                }
                            }}
                        >
                            <ToggleButton value="today" sx={{ color: '#fff' }}>امروز</ToggleButton>
                            <ToggleButton value="week" sx={{ color: '#fff' }}>این هفته</ToggleButton>
                            <ToggleButton value="month" sx={{ color: '#fff' }}>این ماه</ToggleButton>
                        </ToggleButtonGroup>
                        <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>یا بازه دلخواه خود را انتخاب کنید:</Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Calendar
                                value={tempDates}
                                onChange={newDates => { if (Array.isArray(newDates) && newDates.length > 0) { setTempDates(newDates); setActiveFilter('custom'); } }}
                                range
                                calendar={persian}
                                locale={persian_fa}
                                className="bg-dark"
                            />
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}><Button onClick={handleClosePopover}>لغو</Button><Button variant="contained" onClick={handleApplyFilters}>اعمال</Button></Box>
                    </Stack>
                )}
                {popoverType === 'compare' && (
                    <Stack spacing={2} sx={{ minWidth: 300 }}>
                        <Typography variant="h6">تنظیمات مقایسه</Typography>
                        <FormControlLabel control={<Switch checked={tempCompare} onChange={(e) => setTempCompare(e.target.checked)} />} label="مقایسه با دوره قبل" />
                        <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: -1 }}>آمار بازه انتخابی با دوره مشابه قبل از آن مقایسه خواهد شد.</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}><Button onClick={handleClosePopover}>لغو</Button><Button variant="contained" onClick={handleApplyFilters}>اعمال</Button></Box>
                    </Stack>
                )}
                {popoverType === 'refresh' && (
                    <Stack spacing={2} sx={{ minWidth: 300 }}>
                        <Typography variant="h6">بروزرسانی خودکار</Typography>
                        <FormControlLabel control={<Switch checked={autoRefresh.enabled} onChange={(e) => setAutoRefresh(s => ({ ...s, enabled: e.target.checked }))} />} label="فعال‌سازی" />
                        <Collapse in={autoRefresh.enabled}>
                            <ToggleButtonGroup
                                value={autoRefresh.interval}
                                exclusive
                                onChange={(_, val) => { if (val) setAutoRefresh(s => ({ ...s, interval: val })); }}
                                fullWidth
                                color="primary"
                                sx={{
                                    gap: '10px',
                                    '& .MuiToggleButton-root': {
                                        borderRadius: '10px !important',
                                        border: '1px solid rgba(255,255,255,0.23) !important'
                                    }
                                }}
                            >
                                <ToggleButton value={30000} sx={{ color: '#fff' }}>30 ثانیه</ToggleButton>
                                <ToggleButton value={60000} sx={{ color: '#fff' }}>1 دقیقه</ToggleButton>
                                <ToggleButton value={300000} sx={{ color: '#fff' }}>5 دقیقه</ToggleButton>
                            </ToggleButtonGroup>
                        </Collapse>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}>
                            <Button onClick={handleClosePopover}>بستن</Button>
                        </Box>
                    </Stack>
                )}
                {popoverType === 'download' && (
                    <Stack spacing={2} sx={{ minWidth: 320 }}>
                        <Typography variant="h6">دانلود گزارش</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>ابتدا بازه زمانی مورد نظر برای گزارش را انتخاب کنید.</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Calendar
                                value={reportDates}
                                onChange={setReportDates}
                                range
                                calendar={persian}
                                locale={persian_fa}
                                className="bg-dark"
                            />
                        </Box>
                        <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
                            <Button fullWidth variant="contained" color="success" startIcon={<GridOnIcon />} onClick={() => handleDownload('excel')}>اکسل (CSV)</Button>
                            <Button fullWidth variant="contained" color="error" startIcon={<PictureAsPdfIcon />} onClick={() => handleDownload('pdf')}>PDF</Button>
                        </Stack>
                    </Stack>
                )}
            </Popover>
        </>
    );
};

export default FilterBar;